import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { FreshnessBadge } from "@/components/breakout/FreshnessBadge";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import marketOpportunityService, {
  type MarketOpportunitySearchParams,
} from "@/services/marketOpportunityService";
import type {
  BreakoutDetailResponse,
  BreakoutRow,
  BreakoutStatus,
  MarketOpportunityRow,
  MarketOpportunitySegment,
} from "@/types";
import { extractErrorMessage } from "@/utils/errors";
import { BreakoutBreakdown } from "@/components/breakout/BreakoutBreakdown";
import StockResearchDialog from "@/components/StockResearchDialog";
import Rsi2PullbackPage from "@/pages/Rsi2PullbackPage";
import VolumeObvBreakoutPage from "@/pages/VolumeObvBreakoutPage";

// Stock Scoring Standardization (2026-09-13): this page now reads the same
// canonical Breakout Score Engine V2 (`BreakoutStatus`) every other
// Investing Idea sub-page reads — see `BREAKOUT_STATUS_COLORS`/
// `BREAKOUT_STATUS_LABELS`/`BreakoutStatusBadge` below (already used by the
// Breakouts tab; the retired Market-Scanner-based `SentimentBadge`/
// `ScannerSignal` palette is gone, not duplicated).

// Clean 1-Tier Flat Navigation (2026-09-14): the earlier two-level nav
// ("Detailed List" removed first, leaving Breakouts / Top Breakout / Shift
// To Breakout / Market Cap Segments as a top row with a SECOND nested row
// of segment tabs under "Market Cap Segments") is flattened one level
// further — "Market Cap Segments" is no longer a parent/wrapper tab at all.
// Its 6 children (Top Opportunities/NIFTY 50/Large/Mid/Small/Micro Cap) are
// promoted to be peers of Breakouts/Top Breakout/Shift To Breakout in ONE
// single-tier row, plus a brand-new "All" view (the general, unfiltered
// `marketOpportunityService.list(undefined, …)` endpoint, sorted by
// Breakout Score by default — same table/columns as Top Breakout/Shift To
// Breakout). `MarketOpportunityViewMode` is now the union of: "all",
// "breakouts", the two literal `MarketOpportunityTab` strings, and the 6
// literal `MarketOpportunitySegment` strings — no new tab vocabulary for
// the segment values, just no longer nested under a parent tab.
type MarketOpportunityViewMode =
  | "all"
  | "breakouts"
  | "top_breakout"
  | "shift_to_breakout"
  | "rsi2-pullback"
  | "volume-obv-breakout"
  | MarketOpportunitySegment;

// The 6 segment values, reused both to type-guard `viewMode` and to render
// their entries in `FLAT_VIEW_TABS` below — single source of truth so the
// two can never drift apart.
const SEGMENT_VIEW_VALUES: readonly MarketOpportunitySegment[] = [
  "top",
  "nifty-50",
  "large-cap",
  "mid-cap",
  "small-cap",
  "micro-cap",
];

function isSegmentView(value: MarketOpportunityViewMode): value is MarketOpportunitySegment {
  return (SEGMENT_VIEW_VALUES as readonly string[]).includes(value);
}

// Required order: All, Breakouts, Top Breakout, Shift to Breakout, Top
// Opportunities, NIFTY 50, Large Cap, Mid Cap, Small Cap, Micro Cap — all
// ten as peers in one flat bar. `dividerBefore` renders a subtle vertical
// divider immediately before that entry, separating the "Breakout Views"
// group (All/Breakouts/Top Breakout/Shift to Breakout) from the "Market
// Segments" group (Top Opportunities…Micro Cap) while keeping everything
// on the same horizontal tier.
const FLAT_VIEW_TABS: { value: MarketOpportunityViewMode; label: string; dividerBefore?: boolean }[] = [
  { value: "all", label: "All" },
  { value: "breakouts", label: "Breakouts" },
  { value: "top_breakout", label: "Top Breakout" },
  { value: "shift_to_breakout", label: "Shift to Breakout" },
  // Volume + OBV Breakout Scanner (screening only). Placed with the other
  // breakout views (not at the end of the row): the row overflows
  // horizontally with no visible scrollbar, and a tab placed after
  // "RSI(2) Pullback" was cut off-screen and unreachable without scrolling.
  { value: "volume-obv-breakout", label: "Volume + OBV Breakout" },
  { value: "top", label: "Top Opportunities", dividerBefore: true },
  { value: "nifty-50", label: "NIFTY 50" },
  { value: "large-cap", label: "Large Cap" },
  { value: "mid-cap", label: "Mid Cap" },
  { value: "small-cap", label: "Small Cap" },
  { value: "micro-cap", label: "Micro Cap" },
  // RSI(2) Oversold Pullback (Investment Ideas research/screening model,
  // NOT an Algo Strategy) — moved here from being a top-level Investing
  // Idea tab (see `InvestingIdeaPage.tsx`'s own history) so it lives
  // alongside every other Investment Ideas screening view rather than as
  // a peer of Market Opportunities/Portfolio Tracker/Stock Data
  // themselves. `dividerBefore` starts a third visual group, separate
  // from "Breakout Views" and "Market Segments" above.
  { value: "rsi2-pullback", label: "RSI(2) Pullback", dividerBefore: true },
];

const VALID_VIEW_MODES: readonly string[] = FLAT_VIEW_TABS.map((t) => t.value);

// Breakout Engine V1 — dark green = strongest confirmation, grey = nothing
// confirmed. Reused across this whole page (Breakouts tab, Details List,
// Market Cap Segments/NIFTY 50) since Stock Scoring Standardization made
// this the single canonical status vocabulary everywhere.
const BREAKOUT_STATUS_COLORS: Record<BreakoutStatus, string> = {
  STRONG_BREAKOUT: "#1B5E20",
  BREAKOUT_CANDIDATE: "#2E7D32",
  DEVELOPING_BREAKOUT: "#F9A825",
  NO_CONFIRMED_BREAKOUT: "#9E9E9E",
  INSUFFICIENT_DATA: "#5C6BC0",
  FAILED_BREAKOUT: "#C62828",
};

const BREAKOUT_STATUS_LABELS: Record<BreakoutStatus, string> = {
  STRONG_BREAKOUT: "Strong Breakout",
  BREAKOUT_CANDIDATE: "Breakout Candidate",
  DEVELOPING_BREAKOUT: "Developing Breakout",
  NO_CONFIRMED_BREAKOUT: "No Confirmed Breakout",
  INSUFFICIENT_DATA: "Insufficient Data",
  FAILED_BREAKOUT: "Failed Breakout",
};

function BreakoutStatusBadge({ status }: { status: BreakoutStatus }) {
  return (
    <Chip
      label={BREAKOUT_STATUS_LABELS[status]}
      size="small"
      sx={{ bgcolor: BREAKOUT_STATUS_COLORS[status], color: "#fff", fontWeight: 600 }}
    />
  );
}

// Breakout Score Engine V2 (2026-09-13 UI optimization; relocated
// 2026-09-14) — the expandable drawer's "7 weighted indicators + Guardrails
// summary" UI is now shared with the new `StockResearchDialog` (opened from
// Top Opportunities, Market Cap Segments, NIFTY 50, and Portfolio Tracker),
// so it lives in `@/components/breakout/BreakoutBreakdown` and is imported
// above rather than defined here. This is a pure relocation — the fetch
// (`toggleBreakoutRow` below), the lazy-per-row-on-expand behavior, and the
// rendered output are all unchanged.

function formatDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

const SORT_OPTIONS: { value: NonNullable<MarketOpportunitySearchParams["sort_by"]>; label: string }[] = [
  { value: "breakout_score", label: "Breakout Score" },
  { value: "close_price", label: "Close Price" },
  { value: "market_cap_cr", label: "Market Cap" },
  { value: "status_since_date", label: "Status Since" },
];

function num(value: string | null | undefined, decimals = 2): string {
  if (value === null || value === undefined) return "—";
  const n = Number.parseFloat(value);
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString("en-IN", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

/** The canonical 0-100 Breakout Score, color-coded to match its `status`
 * badge (same `BREAKOUT_STATUS_COLORS` the Breakouts tab already uses) —
 * a first-time investor sees at a glance whether this is a strong number
 * without needing to read the badge separately. */
function BreakoutScoreCell({ value, status }: { value: string; status: BreakoutStatus }) {
  const n = Number.parseFloat(value);
  return (
    <Typography variant="body2" fontWeight={700} sx={{ color: BREAKOUT_STATUS_COLORS[status] }}>
      {Number.isNaN(n) ? "—" : Math.round(n)}
    </Typography>
  );
}

/**
 * Phase 5 (Market Cap Segments) + NIFTY 50: one ranked table for a segment
 * view — Ticker, Company Name, Close Price, Breakout Score, Status, Market
 * Cap, Last Updated. Stock Scoring Standardization (2026-09-13): a SINGLE
 * Top-10-by-canonical-score list, replacing the old separate Strong
 * Bullish / Strong Bearish side-by-side tables (the bearish side was
 * retired platform-wide — see `MarketOpportunityRow`'s own docstring).
 */
function SegmentTable({
  title,
  rows,
  onRowClick,
}: {
  title: string;
  rows: MarketOpportunityRow[];
  onRowClick: (symbol: string) => void;
}) {
  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, flex: 1, minWidth: 0 }}>
      <Box px={2} pt={2}>
        <Typography variant="subtitle1" fontWeight={700}>
          {title}
        </Typography>
      </Box>
      {rows.length === 0 ? (
        <Box p={3} textAlign="center">
          <Typography variant="body2" color="text.secondary">
            Nothing here yet.
          </Typography>
        </Box>
      ) : (
        <TableContainer sx={{ p: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Ticker</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Company Name</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                  Close Price
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                  Breakout Score
                </TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                  Market Cap
                </TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>Last Updated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.symbol} hover onClick={() => onRowClick(row.symbol)} sx={{ cursor: "pointer" }}>
                  <TableCell sx={{ fontWeight: 600 }}>{row.symbol}</TableCell>
                  <TableCell>{row.company_name}</TableCell>
                  <TableCell align="right">₹{num(row.close_price)}</TableCell>
                  <TableCell align="right">
                    <BreakoutScoreCell value={row.breakout_score} status={row.status} />
                  </TableCell>
                  <TableCell>
                    <BreakoutStatusBadge status={row.status} /><FreshnessBadge data={row} />
                  </TableCell>
                  <TableCell align="right">
                    {row.market_cap ? `₹${num(row.market_cap, 0)} Cr` : "—"}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDateTime(row.last_updated)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}

export function MarketOpportunitiesPage() {
  // Supports being deep-linked from elsewhere in the app (e.g. the
  // Portfolio Tracker's "View Analysis" link) via `?symbol=RELIANCE`, which
  // just pre-fills the existing search box — default behavior when this
  // param is absent (the normal case, navigating here from the sidebar) is
  // completely unchanged. `?view=` (added with the sub-nav flattening,
  // 2026-09-14) restores whichever of the 4 top-level views was active,
  // same "read once at mount, write back via replace" convention
  // `InvestingIdeaPage.tsx`'s own `?tab=` sync already uses.
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSymbol = searchParams.get("symbol") ?? "";
  const [search, setSearch] = useState(initialSymbol);
  const [searchDraft, setSearchDraft] = useState(initialSymbol);
  const [sortBy, setSortBy] = useState<NonNullable<MarketOpportunitySearchParams["sort_by"]>>("breakout_score");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  const [rows, setRows] = useState<MarketOpportunityRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Clean 1-Tier Flat Navigation (2026-09-14): 10 independent view modes on
  // this SAME page (All / Breakouts / Top Breakout / Shift To Breakout / the
  // 6 segment views), each with its own fetch, loading, and error state so
  // switching between them never disturbs another view's data or in-flight
  // request. `initialView` restores a bookmarked/shared `?view=` link;
  // falls back to "top_breakout" (this page's long-standing default landing
  // view) for anything absent or unrecognized.
  const initialView = searchParams.get("view");
  const [viewMode, setViewModeState] = useState<MarketOpportunityViewMode>(
    initialView && VALID_VIEW_MODES.includes(initialView)
      ? (initialView as MarketOpportunityViewMode)
      : "top_breakout"
  );
  // Keeps the URL in sync (replace, not push, so switching tabs doesn't
  // spam browser history) — clicking a tab is the only way `viewMode`
  // changes, so this is the single place that needs to write it back.
  const setViewMode = useCallback(
    (next: MarketOpportunityViewMode) => {
      setViewModeState(next);
      setSearchParams(
        (prev) => {
          const nextParams = new URLSearchParams(prev);
          nextParams.set("view", next);
          return nextParams;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );
  const [segmentItems, setSegmentItems] = useState<MarketOpportunityRow[]>([]);
  const [segmentLoading, setSegmentLoading] = useState(false);
  const [segmentError, setSegmentError] = useState<string | null>(null);

  // Breakout Engine V1 — its own fetch/loading/error state, same pattern as
  // the Segments view above: switching TO this view never disturbs the
  // Top Breakout/Shift To Breakout table's own data or in-flight request,
  // and vice versa.
  const [breakoutRows, setBreakoutRows] = useState<BreakoutRow[]>([]);
  const [breakoutLoading, setBreakoutLoading] = useState(false);
  const [breakoutError, setBreakoutError] = useState<string | null>(null);
  // Client-side pagination -- the backend always returns the whole scored
  // list in one response (no server-side page/page_size params on this
  // endpoint, unlike the Top Breakout/Shift To Breakout table), so this
  // just slices what's already in memory. Same rowsPerPageOptions
  // convention as that table's own TablePagination below, defaulting to 50.
  const [breakoutPage, setBreakoutPage] = useState(0);
  const [breakoutPageSize, setBreakoutPageSize] = useState(50);
  // Sector/Industry filter (2026-09-13 UI optimization) — purely a
  // client-side filter over the already-fetched `breakoutRows` (the
  // backend always returns the whole scored universe in one response, same
  // as before); "ALL" is the sentinel for "All Sectors ▾" / no filter.
  // `sector` is only ever populated on `StockMaster` for the original
  // NIFTY 50 seed (migration 0005) — every stock added later via the bulk
  // NSE/BSE security-master import has no sector/industry source to draw
  // from at all, so it's genuinely `null`, not a bug. "UNCATEGORIZED" is a
  // real, honest filter bucket for those stocks rather than hiding them.
  const [breakoutSectorFilter, setBreakoutSectorFilter] = useState<string>("ALL");
  const UNCATEGORIZED_SECTOR = "UNCATEGORIZED";

  // Sortable columns on the Breakouts table (2026-09-13) — client-side,
  // same reasoning as the sector filter: this endpoint returns the whole
  // scored universe in one response already sorted by score, so re-sorting
  // client-side needs no new backend params. Defaults preserve the
  // existing "highest score first" behavior.
  const [breakoutSortBy, setBreakoutSortBy] = useState<"score" | "sector">("score");
  const [breakoutSortOrder, setBreakoutSortOrder] = useState<"asc" | "desc">("desc");

  const handleBreakoutSort = (field: "score" | "sector") => {
    if (breakoutSortBy === field) {
      setBreakoutSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setBreakoutSortBy(field);
      setBreakoutSortOrder(field === "score" ? "desc" : "asc");
    }
  };

  // Breakout Score Engine V2 (2026-09-13) — expandable per-row breakdown.
  // Detail is fetched lazily, ONE symbol at a time, only when that row is
  // actually expanded (see spec: "Add an expandable row/drawer only when
  // the user wants details") — never as part of the base list fetch above.
  const [expandedSymbol, setExpandedSymbol] = useState<string | null>(null);
  const [breakoutDetailBySymbol, setBreakoutDetailBySymbol] = useState<Record<string, BreakoutDetailResponse>>({});
  const [breakoutDetailLoading, setBreakoutDetailLoading] = useState<Record<string, boolean>>({});
  const [breakoutDetailError, setBreakoutDetailError] = useState<Record<string, string>>({});

  const toggleBreakoutRow = async (symbol: string) => {
    if (expandedSymbol === symbol) {
      setExpandedSymbol(null);
      return;
    }
    setExpandedSymbol(symbol);
    if (breakoutDetailBySymbol[symbol] || breakoutDetailLoading[symbol]) return;
    setBreakoutDetailLoading((prev) => ({ ...prev, [symbol]: true }));
    setBreakoutDetailError((prev) => {
      const next = { ...prev };
      delete next[symbol];
      return next;
    });
    try {
      const detail = await marketOpportunityService.getBreakoutDetail(symbol);
      setBreakoutDetailBySymbol((prev) => ({ ...prev, [symbol]: detail }));
    } catch (err) {
      setBreakoutDetailError((prev) => ({ ...prev, [symbol]: extractErrorMessage(err) }));
    } finally {
      setBreakoutDetailLoading((prev) => ({ ...prev, [symbol]: false }));
    }
  };

  // 400ms debounce on the search box, same convention used elsewhere in this
  // app, so every keystroke doesn't fire a request.
  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchDraft), 400);
    return () => clearTimeout(handle);
  }, [searchDraft]);

  useEffect(() => {
    setPage(0);
  }, [viewMode, search]);

  // Table data (Top Breakout / Shift To Breakout — the two views that share
  // this page's original full-column table) is only ever fetched while one
  // of those two views is actually active, so switching to Breakouts or
  // Market Cap Segments never triggers a request for data the user isn't
  // looking at. The 4 status-band summary counters are no longer read from
  // this response at all — they were only ever populated as a side effect
  // of this specific fetch, which meant they showed stuck at zero while
  // browsing Breakouts/any Market Cap Segment. They're now fetched
  // independently, once, by `InvestingIdeaPage` (which renders them
  // right-aligned in its own tab row) — see that file.
  const load = useCallback(async () => {
    if (viewMode !== "top_breakout" && viewMode !== "shift_to_breakout" && viewMode !== "all") return;
    setLoading(true);
    setError(null);
    try {
      // "All" (new, Clean 1-Tier Flat Navigation) calls the same general,
      // unfiltered endpoint `tab: undefined` has always hit — see
      // `marketOpportunityService.list`'s own docstring ("tab omitted...
      // returns all statuses — used for the 'All' view, if any"). Sorted by
      // Breakout Score by default via this component's existing `sortBy`
      // initial state, same as every other view here.
      const tab = viewMode === "all" ? undefined : viewMode;
      const response = await marketOpportunityService.list(tab, {
        search: search || undefined,
        page: page + 1,
        page_size: pageSize,
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      setRows(response.items);
      setTotal(response.meta.total);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [viewMode, search, page, pageSize, sortBy, sortOrder]);

  useEffect(() => {
    load();
  }, [load]);

  const loadSegment = useCallback(async () => {
    if (!isSegmentView(viewMode)) return;
    setSegmentLoading(true);
    setSegmentError(null);
    try {
      const response = await marketOpportunityService.getSegment(viewMode);
      setSegmentItems(response.items);
    } catch (err) {
      setSegmentError(extractErrorMessage(err));
    } finally {
      setSegmentLoading(false);
    }
  }, [viewMode]);

  // Only fetches once the user actually switches to one of the 6 (now
  // flattened, peer top-level) segment views — the Top Breakout/Shift To
  // Breakout/All table's own `load()` effect above is unaffected either way.
  useEffect(() => {
    if (isSegmentView(viewMode)) {
      loadSegment();
    }
  }, [viewMode, loadSegment]);

  // Breakout Engine V1 — same lazy-fetch-on-switch pattern as loadSegment
  // above. No params: the backend always scores and returns every stock it
  // can, already sorted highest score first.
  const loadBreakouts = useCallback(async () => {
    setBreakoutLoading(true);
    setBreakoutError(null);
    try {
      const response = await marketOpportunityService.getBreakouts();
      setBreakoutRows(response.items);
      setBreakoutPage(0);
    } catch (err) {
      setBreakoutError(extractErrorMessage(err));
    } finally {
      setBreakoutLoading(false);
    }
  }, []);

  useEffect(() => {
    if (viewMode === "breakouts") {
      loadBreakouts();
    }
  }, [viewMode, loadBreakouts]);

  // Sector/Industry filter options — derived purely from whatever sectors
  // are actually present in this load's scored rows, sorted alphabetically;
  // no separate lookup/endpoint. "Uncategorized" is appended last (a data
  // bucket, not a real sector) only when at least one loaded row actually
  // lacks a sector, so the option never appears once the data gap is closed.
  const breakoutHasUncategorized = useMemo(() => breakoutRows.some((r) => !r.sector), [breakoutRows]);
  const breakoutSectorOptions = useMemo(() => {
    const sectors = new Set<string>();
    for (const row of breakoutRows) {
      if (row.sector) sectors.add(row.sector);
    }
    return Array.from(sectors).sort((a, b) => a.localeCompare(b));
  }, [breakoutRows]);

  useEffect(() => {
    setBreakoutPage(0);
  }, [breakoutSectorFilter, breakoutSortBy, breakoutSortOrder]);

  // Investing Idea architecture finalization (2026-09-14): clicking a stock
  // row anywhere on this page (Details List, Market Cap Segments/NIFTY 50,
  // Breakouts) opens the shared `StockResearchDialog` in place — replacing
  // the earlier "jump to the Detailed List and pre-fill the search box"
  // hack (`openStockDetail`) that used to back the Breakouts tab's row
  // click. No new route/page: this is the "Stock Research" detail view,
  // reused across every entry point per the finalized Investing Idea
  // architecture. Reuses the existing `getStockHistory`/`getBreakoutDetail`
  // APIs only — no new backend endpoint, no score calculated in React.
  const [researchSymbol, setResearchSymbol] = useState<string | null>(null);

  const handleSortByChange = (e: SelectChangeEvent) => {
    setSortBy(e.target.value as NonNullable<MarketOpportunitySearchParams["sort_by"]>);
  };

  const handleSort = (field: NonNullable<MarketOpportunitySearchParams["sort_by"]>) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  const columns: {
    field: NonNullable<MarketOpportunitySearchParams["sort_by"]> | "symbol" | "status";
    label: string;
    sortable: boolean;
  }[] = [
    { field: "symbol", label: "Ticker", sortable: false },
    { field: "close_price", label: "Close Price", sortable: true },
    { field: "breakout_score", label: "Breakout Score", sortable: true },
    { field: "market_cap_cr", label: "Market Cap (Cr)", sortable: true },
    { field: "status", label: "Status", sortable: false },
    { field: "status_since_date", label: "Status Since", sortable: true },
  ];

  return (
    <Box>
      {/* ---- Clean 1-Tier Flat Navigation on its own dedicated row — no page
          title here: "MARKET OPPORTUNITIES" is already shown, selected, by
          `InvestingIdeaPage`'s own outer tab row immediately above this
          component, so repeating it here was a duplicate header. The 4
          status summary counters that used to sit alongside a title in this
          page have also moved out — they now render right-aligned in that
          same outer tab row (see `InvestingIdeaPage.tsx`), fetched
          independently of whichever view is active below, instead of being
          stuck at zero except while viewing "All".

          The row below is the flat nav itself: All / Breakouts / Top
          Breakout / Shift to Breakout / Top Opportunities / NIFTY 50 /
          Large / Mid / Small / Micro Cap, all as peers in one
          wrapping strip (it wraps to a second line instead of clipping chips) (a subtle vertical
          divider separates "Breakout Views" from "Market Segments").
          `flexShrink: 0` on every chip (and the divider) so labels like
          "NIFTY 50" are never clipped — the row scrolls instead of
          shrinking its children. ---- */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          alignItems: "center",
          mb: 2,
          pb: 0.5,
        }}
      >
        {FLAT_VIEW_TABS.map((t) => (
          <Fragment key={t.value}>
            {t.dividerBefore && (
              <Divider orientation="vertical" flexItem sx={{ mx: 0.25, my: 0.5, flexShrink: 0 }} />
            )}
            <Chip
              label={t.label}
              clickable
              color={viewMode === t.value ? "primary" : "default"}
              variant={viewMode === t.value ? "filled" : "outlined"}
              onClick={() => setViewMode(t.value)}
              sx={{
                fontWeight: 600,
                flexShrink: 0,
                height: 28,
                fontSize: "0.8125rem",
                "& .MuiChip-label": { px: 1.25 },
              }}
            />
          </Fragment>
        ))}
      </Box>

      {isSegmentView(viewMode) ? (
        <>
          {/* ---- Market Cap Segment view (Top Opportunities / NIFTY 50 /
              Large / Mid / Small / Micro Cap) — the segment picker itself is
              now one of the flat top-level tabs above, not a second nested
              row, so this is just a refresh control + the fixed Top-10 table. ---- */}
          <Paper variant="outlined" sx={{ borderRadius: 3, mb: 2, px: 2, py: 1 }}>
            <Stack direction="row" justifyContent="flex-end" alignItems="center">
              <Tooltip title="Refresh">
                <IconButton onClick={loadSegment} disabled={segmentLoading} size="small">
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Paper>

          {segmentError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSegmentError(null)}>
              {segmentError}
            </Alert>
          )}

          {segmentLoading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : (
            <SegmentTable title="Top 10 by Breakout Score" rows={segmentItems} onRowClick={setResearchSymbol} />
          )}
        </>
      ) : viewMode === "breakouts" ? (
        <>
          {/* ---- Breakout Score Engine V2: KEEP THE TABLE ITSELF SIMPLE per
              spec — Ticker | Breakout Score | Status, reading pre-calculated
              results only (no computation on this page-load path anymore).
              The chevron expands a per-row drawer (fetched on demand) with
              the full Indicator | Raw Value | Points | Status breakdown;
              the score itself carries a tooltip with a compact contribution
              summary. Rows scored below 60 ("No Confirmed Breakout") are
              filtered out client-side: this tab only surfaces candidates
              worth a look. Clicking elsewhere on a row opens the shared
              `StockResearchDialog` (see below). ---- */}
          <Paper variant="outlined" sx={{ borderRadius: 3, mb: 2, px: 2, py: 1 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel id="breakout-sector-label">All Sectors</InputLabel>
                <Select
                  labelId="breakout-sector-label"
                  label="All Sectors"
                  value={breakoutSectorFilter}
                  onChange={(e) => setBreakoutSectorFilter(e.target.value)}
                >
                  <MenuItem value="ALL">All Sectors</MenuItem>
                  {breakoutSectorOptions.map((sector) => (
                    <MenuItem key={sector} value={sector}>
                      {sector}
                    </MenuItem>
                  ))}
                  {breakoutHasUncategorized && (
                    <MenuItem value={UNCATEGORIZED_SECTOR}>Uncategorized</MenuItem>
                  )}
                </Select>
              </FormControl>
              <Tooltip title="Refresh">
                <IconButton onClick={loadBreakouts} disabled={breakoutLoading} size="small">
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Paper>

          {breakoutError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setBreakoutError(null)}>
              {breakoutError}
            </Alert>
          )}

          {breakoutLoading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : (
            (() => {
              const filteredBreakouts = breakoutRows.filter((r) => {
                if (r.status === "NO_CONFIRMED_BREAKOUT") return false;
                if (breakoutSectorFilter === "ALL") return true;
                if (breakoutSectorFilter === UNCATEGORIZED_SECTOR) return !r.sector;
                return r.sector === breakoutSectorFilter;
              });
              const visibleBreakouts = [...filteredBreakouts].sort((a, b) => {
                let cmp: number;
                if (breakoutSortBy === "sector") {
                  cmp = (a.sector ?? "Uncategorized").localeCompare(b.sector ?? "Uncategorized");
                } else {
                  cmp = a.breakout_score - b.breakout_score;
                }
                return breakoutSortOrder === "asc" ? cmp : -cmp;
              });
              const pagedBreakouts = visibleBreakouts.slice(
                breakoutPage * breakoutPageSize,
                breakoutPage * breakoutPageSize + breakoutPageSize
              );
              return visibleBreakouts.length === 0 ? (
                <Paper variant="outlined" sx={{ borderRadius: 3, p: 6, textAlign: "center" }}>
                  <Typography variant="h6" gutterBottom>
                    No confirmed breakouts right now
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {breakoutSectorFilter !== "ALL"
                      ? "No confirmed breakouts in this sector — try All Sectors or another tab."
                      : "Check back after the next data update, or try another tab."}
                  </Typography>
                </Paper>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3, maxHeight: 640 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 40 }} />
                        <TableCell sx={{ fontWeight: 700 }}>Ticker</TableCell>
                        <TableCell
                          onClick={() => handleBreakoutSort("sector")}
                          sx={{ fontWeight: 700, whiteSpace: "nowrap", cursor: "pointer" }}
                        >
                          Sector / Industry
                          {breakoutSortBy === "sector" ? (breakoutSortOrder === "asc" ? " ▲" : " ▼") : ""}
                        </TableCell>
                        <TableCell
                          align="right"
                          onClick={() => handleBreakoutSort("score")}
                          sx={{ fontWeight: 700, whiteSpace: "nowrap", cursor: "pointer" }}
                        >
                          Breakout Score
                          {breakoutSortBy === "score" ? (breakoutSortOrder === "asc" ? " ▲" : " ▼") : ""}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pagedBreakouts.map((row) => {
                        const isExpanded = expandedSymbol === row.symbol;
                        return (
                          <Fragment key={row.symbol}>
                            <TableRow
                              hover
                              onClick={() => setResearchSymbol(row.symbol)}
                              sx={{ cursor: "pointer" }}
                            >
                              <TableCell sx={{ p: 0.5 }}>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleBreakoutRow(row.symbol);
                                  }}
                                >
                                  {isExpanded ? (
                                    <KeyboardArrowUpIcon fontSize="small" />
                                  ) : (
                                    <KeyboardArrowDownIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>{row.symbol}</TableCell>
                              <TableCell sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
                                {row.sector ?? (
                                  <Tooltip title="No sector/industry classification exists yet for this stock — only the original NIFTY 50 list has this data today.">
                                    <span>Uncategorized</span>
                                  </Tooltip>
                                )}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700 }}>
                                <Tooltip
                                  title="30 Resistance + 20 Volume + 15 EMA Alignment + 10 Base/Squeeze + 10 Relative Strength (vs NIFTY 50) + 10 RSI + 5 ADX, minus an Upper-Wick penalty and an Anchored-VWAP cap when either false-breakout filter fires. Expand the row for this stock's exact contribution."
                                  arrow
                                >
                                  <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                                    <span>{Math.round(row.breakout_score)}</span>
                                    <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.disabled" }} />
                                  </Stack>
                                </Tooltip>
                              </TableCell>
                              <TableCell>
                                <BreakoutStatusBadge status={row.status} /><FreshnessBadge data={row} />
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell colSpan={5} sx={{ p: 0, borderBottom: isExpanded ? undefined : "none" }}>
                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                  <BreakoutBreakdown
                                    detail={breakoutDetailBySymbol[row.symbol]}
                                    loading={Boolean(breakoutDetailLoading[row.symbol])}
                                    error={breakoutDetailError[row.symbol]}
                                  />
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                  <TablePagination
                    component="div"
                    count={visibleBreakouts.length}
                    page={breakoutPage}
                    onPageChange={(_e, newPage) => setBreakoutPage(newPage)}
                    rowsPerPage={breakoutPageSize}
                    rowsPerPageOptions={[25, 50, 100]}
                    onRowsPerPageChange={(e) => {
                      setBreakoutPageSize(parseInt(e.target.value, 10));
                      setBreakoutPage(0);
                    }}
                  />
                </TableContainer>
              );
            })()
          )}
        </>
      ) : viewMode === "rsi2-pullback" ? (
        // RSI(2) Oversold Pullback — an independent research/screening
        // page with its own data fetching, pagination, and detail dialog
        // (see `Rsi2PullbackPage.tsx`). Rendered as-is, with no extra
        // wrapper: it already renders its own title/description/
        // disclaimer, matching how this flat-nav row lets each view own
        // its full content area below the chips.
        <Rsi2PullbackPage />
      ) : viewMode === "volume-obv-breakout" ? (
        // Volume + OBV Breakout Scanner -- independent screening view with
        // its own fetching/filters (see `VolumeObvBreakoutPage.tsx`).
        <VolumeObvBreakoutPage />
      ) : (
        <>
          {/* ---- Search/Sort/Refresh (the tab chips themselves now live in
              the shared sub-navigation row above, not duplicated here) ---- */}
          <Paper variant="outlined" sx={{ borderRadius: 3, mb: 2, px: 1 }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="flex-end"
              alignItems={{ md: "center" }}
              spacing={1}
              py={1}
            >
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
                <TextField
                  label="Search by Symbol"
                  size="small"
                  value={searchDraft}
                  onChange={(e) => setSearchDraft(e.target.value)}
                  sx={{ minWidth: 220 }}
                />
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel id="sort-by-label">Sort by</InputLabel>
                  <Select labelId="sort-by-label" label="Sort by" value={sortBy} onChange={handleSortByChange}>
                    {SORT_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Tooltip title="Refresh">
                  <IconButton onClick={load} disabled={loading}>
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </Paper>


      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Paper variant="outlined" sx={{ borderRadius: 3, p: 6, textAlign: "center" }}>
          <Typography variant="h6" gutterBottom>
            No opportunities here yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {search
              ? "No stock matched your search — try a different symbol."
              : "Nothing fits this tab right now. Try another tab, or check back after the next data update."}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3, maxHeight: 640 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell
                    key={col.field}
                    align={col.field === "symbol" || col.field === "status" ? "left" : "right"}
                    onClick={col.sortable ? () => handleSort(col.field as never) : undefined}
                    sx={{ cursor: col.sortable ? "pointer" : "default", whiteSpace: "nowrap", fontWeight: 700 }}
                  >
                    {col.label}
                    {col.sortable && sortBy === col.field ? (sortOrder === "asc" ? " ▲" : " ▼") : ""}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.symbol} hover onClick={() => setResearchSymbol(row.symbol)} sx={{ cursor: "pointer" }}>
                  <TableCell sx={{ fontWeight: 600 }}>{row.symbol}</TableCell>
                  <TableCell align="right">₹{num(row.close_price)}</TableCell>
                  <TableCell align="right">
                    <BreakoutScoreCell value={row.breakout_score} status={row.status} />
                  </TableCell>
                  <TableCell align="right">{num(row.market_cap_cr, 0)}</TableCell>
                  <TableCell>
                    <BreakoutStatusBadge status={row.status} /><FreshnessBadge data={row} />
                  </TableCell>
                  <TableCell align="right">{row.status_since_date ?? "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_e, newPage) => setPage(newPage)}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[25, 50, 100]}
            onRowsPerPageChange={(e) => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </TableContainer>
      )}
        </>
      )}

      {researchSymbol && (
        <StockResearchDialog symbol={researchSymbol} onClose={() => setResearchSymbol(null)} />
      )}
    </Box>
  );
}

export default MarketOpportunitiesPage;
